import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Field } from "@/components/app/Common";
import { SimpleSelect, entityOptions } from "@/components/app/SimpleSelect";
import { api, errMsg } from "@/lib/api";
import { csv } from "@/lib/ui";

const EMPTY = { name: "", jurisdiction: "CA-ON", corp_number: "", bn: "", ein: "", incorporation_date: "", fiscal_year_end: "12-31",
  registered_office: "", directors: "", officers: "", status: "Active", parent_id: "", dissolution_date: "", auto_obligations: true };

export const EntityForm = ({ open, onOpenChange, entity, entities, meta, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setF(entity ? { ...EMPTY, ...entity, directors: (entity.directors || []).join(", "), officers: (entity.officers || []).join(", "), parent_id: entity.parent_id || "" } : EMPTY);
  }, [entity, open]);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const jurs = Object.entries(meta?.jurisdictions || {}).map(([k, v]) => ({ value: k, label: `${v.country === "Canada" ? "CA" : "US"} · ${v.label}` }));
  const isUS = f.jurisdiction.startsWith("US");
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { ...f, directors: csv(f.directors), officers: csv(f.officers), parent_id: f.parent_id || null };
    try {
      if (entity) await api.put(`/entities/${entity.id}`, body);
      else await api.post("/entities", body);
      toast.success(entity ? "Entity updated" : "Entity added — compliance calendar generated");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!window.confirm(`Delete ${entity.name} and all its filings and tax items?`)) return;
    await api.delete(`/entities/${entity.id}`);
    toast.success("Entity removed");
    onSaved();
    onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="entity-form-dialog">
        <DialogHeader><DialogTitle>{entity ? "Edit entity" : "Add corporation"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><Field label="Entity name"><Input required value={f.name} onChange={set("name")} data-testid="entity-name-input" /></Field></div>
          <Field label="Jurisdiction"><SimpleSelect value={f.jurisdiction} onChange={set("jurisdiction")} options={jurs} testId="entity-jurisdiction-select" /></Field>
          <Field label="Status"><SimpleSelect value={f.status} onChange={set("status")} testId="entity-status-select"
            options={["Active", "Inactive", "Dissolved", "Revival In Progress"].map((s) => ({ value: s, label: s }))} /></Field>
          <Field label="Corporation number"><Input value={f.corp_number} onChange={set("corp_number")} data-testid="entity-corp-number-input" /></Field>
          {isUS ? <Field label="EIN"><Input value={f.ein} onChange={set("ein")} placeholder="12-3456789" data-testid="entity-ein-input" /></Field>
            : <Field label="Business Number (BN)"><Input value={f.bn} onChange={set("bn")} placeholder="123456789RC0001" data-testid="entity-bn-input" /></Field>}
          <Field label="Incorporation date"><Input type="date" value={f.incorporation_date} onChange={set("incorporation_date")} data-testid="entity-inc-date-input" /></Field>
          <Field label="Fiscal year end (MM-DD)"><Input value={f.fiscal_year_end} pattern="\d{2}-\d{2}" onChange={set("fiscal_year_end")} data-testid="entity-fye-input" /></Field>
          <div className="sm:col-span-2"><Field label="Registered office"><Input value={f.registered_office} onChange={set("registered_office")} data-testid="entity-office-input" /></Field></div>
          <Field label="Directors (comma separated)"><Input value={f.directors} onChange={set("directors")} data-testid="entity-directors-input" /></Field>
          <Field label="Officers (comma separated)"><Input value={f.officers} onChange={set("officers")} data-testid="entity-officers-input" /></Field>
          <Field label="Parent company"><SimpleSelect value={f.parent_id} onChange={set("parent_id")} allowNone noneLabel="No parent (top level)" testId="entity-parent-select"
            options={entityOptions(entities.filter((x) => x.id !== entity?.id))} /></Field>
          {f.status === "Dissolved" && <Field label="Dissolution date"><Input type="date" value={f.dissolution_date} onChange={set("dissolution_date")} data-testid="entity-dissolution-input" /></Field>}
          {!entity && (
            <label className="sm:col-span-2 flex items-center gap-3 rounded-lg border border-border p-3 text-sm">
              <Switch checked={f.auto_obligations} onCheckedChange={set("auto_obligations")} data-testid="entity-auto-obligations-switch" />
              Auto-generate filings and tax deadlines for this jurisdiction
            </label>
          )}
          <div className="sm:col-span-2 flex justify-between gap-2 pt-2">
            {entity ? <Button type="button" variant="ghost" className="text-red-500" onClick={remove} data-testid="entity-delete-btn">Delete</Button> : <span />}
            <Button type="submit" disabled={busy} data-testid="entity-save-btn">{entity ? "Save changes" : "Add entity"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
